class DrawerSearchSection extends HTMLElement {
  constructor() {
    super();

    /* ============================================================
       BASIC DRAWER CONFIG
       ============================================================ */

    this.isOpen = false;

    this.drawerClass = "wt-drawer-search";
    this.classDrawerActive = `${this.drawerClass}--active`;

    this.pageOverlayClass = "search-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;

    this.body = document.body;

    /*
      IMPORTANT:
      Each <search-drawer> controls ITSELF.

      The old code was using:
      document.querySelector(".wt-drawer-search")

      which always returned the first search drawer on the page.
    */
    this.drawer = this;

    /* ============================================================
       SEARCH ELEMENTS INSIDE THIS DRAWER
       ============================================================ */

    this.input = this.querySelector('input[name="q"]');

    this.clearButton = this.querySelector(
      ".wt-header__search__clear-button"
    );

    this.closeButton = this.querySelector(
      ".wt-header__search__close"
    );

    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );

    this.predictiveSearchStatus = this.querySelector(
      ".predictive-search-status"
    );

    this.emptyAnnouncement = this.querySelector(
      ".search-empty"
    );

    /*
      Internal trigger belonging to THIS drawer.
    */
    this.internalTrigger = this.querySelector(
      ".wt-header__search-trigger"
    );

    /*
      External header-panel search trigger.

      It only belongs to this drawer when:
      aria-controls="this drawer id"
    */
    this.externalTriggers = () => {
      if (!this.id) {
        return [];
      }

      return Array.from(
        document.querySelectorAll(
          ".wt-header-panel-search-trigger"
        )
      ).filter(
        (trigger) =>
          trigger.getAttribute("aria-controls") === this.id
      );
    };

    /*
      Main trigger used for focus restoration.
    */
    this.mainTrigger =
      this.externalTriggers()[0] ||
      this.internalTrigger ||
      null;

    /* ============================================================
       SEARCH CACHE
       ============================================================ */

    this.cachedResults = {};

    this.isVisibleClearButton = false;

    /* ============================================================
       BIND METHODS
       ============================================================ */

    this.saveSuggestionMenuInDesignMode =
      this.saveSuggestionMenuInDesignMode.bind(this);

    this.onFormSubmit =
      this.onFormSubmit.bind(this);

    this.setupEventListeners();

    /* ============================================================
       ACCESSIBILITY
       ============================================================ */

    this.toggleTabindexElements = [
      this.input,
      this.closeButton,
      this.clearButton,
    ].filter(Boolean);

    this.init();
  }

  /* ==============================================================
     FOCUSABLE ELEMENTS
     ============================================================== */

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input:not([type='hidden']), select, [tabindex]";

    const focusableElements = () =>
      Array.from(
        this.querySelectorAll(focusableElementsSelector)
      ).filter(
        (el) =>
          !el.hasAttribute("disabled") &&
          el.tabIndex >= 0
      );

    const elements = focusableElements();

    return {
      focusableElements,
      first: elements[0],
      last: elements[elements.length - 1],
    };
  }

  /* ==============================================================
     SAFE TABINDEX HELPER
     ============================================================== */

  setElementsTabIndex(elements, value) {
    elements
      .filter(Boolean)
      .forEach((element) => {
        element.setAttribute("tabindex", value);
      });
  }

  /* ==============================================================
     OPEN / CLOSE
     ============================================================== */

  onToggle() {
    if (this.hasAttribute("open")) {
      /* ----------------------------------------------------------
         CLOSE
         ---------------------------------------------------------- */

      this.removeAttribute("open");

      this.setElementsTabIndex(
        this.toggleTabindexElements,
        "-1"
      );

      this.setElementsTabIndex(
        [this.mainTrigger],
        "0"
      );

      this.isOpen = false;

      /*
        Update external trigger accessibility state.
      */
      this.externalTriggers().forEach((trigger) => {
        trigger.setAttribute("aria-expanded", "false");
      });

      if (this.internalTrigger) {
        this.internalTrigger.setAttribute(
          "aria-expanded",
          "false"
        );
      }

      /*
        Restore focus safely.
      */
      setTimeout(() => {
        if (
          this.mainTrigger &&
          typeof this.mainTrigger.focus === "function"
        ) {
          this.mainTrigger.focus();
        }
      }, 0);

    } else {
      /* ----------------------------------------------------------
         OPEN
         ---------------------------------------------------------- */

      this.setAttribute("open", "");

      this.setElementsTabIndex(
        this.toggleTabindexElements,
        "0"
      );

      this.setElementsTabIndex(
        [this.mainTrigger],
        "-1"
      );

      this.isOpen = true;

      /*
        Update external trigger accessibility state.
      */
      this.externalTriggers().forEach((trigger) => {
        trigger.setAttribute("aria-expanded", "true");
      });

      if (this.internalTrigger) {
        this.internalTrigger.setAttribute(
          "aria-expanded",
          "true"
        );
      }

      /*
        Focus search input.
      */
      setTimeout(() => {
        if (
          this.input &&
          typeof this.input.focus === "function"
        ) {
          this.input.focus();
        }
      }, 0);
    }
  }

  /* ==============================================================
     DRAWER + OVERLAY
     ============================================================== */

  toggleDrawerClasses() {

  const wasOpen = this.hasAttribute("open");

  /*
   * Close all other search drawers first
   */
  document
    .querySelectorAll("search-drawer.wt-drawer-search")
    .forEach((drawer) => {

      if (drawer !== this) {

        drawer.removeAttribute("open");

        drawer.classList.remove(
          "wt-drawer-search--active"
        );

        drawer.isOpen = false;

        if (drawer.input) {
          drawer.input.setAttribute("tabindex", "-1");
        }

        if (drawer.closeButton) {
          drawer.closeButton.setAttribute("tabindex", "-1");
        }

        if (drawer.internalTrigger) {
          drawer.internalTrigger.setAttribute(
            "aria-expanded",
            "false"
          );
        }

        if (typeof drawer.externalTriggers === "function") {
          drawer.externalTriggers().forEach((trigger) => {
            trigger.setAttribute(
              "aria-expanded",
              "false"
            );
          });
        }
      }
    });


  /*
   * CLOSE
   */
  if (wasOpen) {

    this.removeAttribute("open");

    this.classList.remove(
      "wt-drawer-search--active"
    );

    this.setElementsTabIndex(
      this.toggleTabindexElements,
      "-1"
    );

    this.setElementsTabIndex(
      [this.mainTrigger],
      "0"
    );

    this.isOpen = false;

    this.externalTriggers().forEach((trigger) => {
      trigger.setAttribute(
        "aria-expanded",
        "false"
      );
    });

    if (this.internalTrigger) {
      this.internalTrigger.setAttribute(
        "aria-expanded",
        "false"
      );
    }

    this.body.classList.remove(
      this.activeOverlayBodyClass
    );

    /*
     * Restore focus
     */
    setTimeout(() => {

      if (
        this.mainTrigger &&
        typeof this.mainTrigger.focus === "function"
      ) {
        this.mainTrigger.focus();
      }

    }, 0);

    return;
  }


  /*
   * OPEN
   */
  this.setAttribute("open", "");

  this.classList.add(
    "wt-drawer-search--active"
  );

  this.setElementsTabIndex(
    this.toggleTabindexElements,
    "0"
  );

  this.setElementsTabIndex(
    [this.mainTrigger],
    "-1"
  );

  this.isOpen = true;


  /*
   * Update accessibility
   */
  this.externalTriggers().forEach((trigger) => {
    trigger.setAttribute(
      "aria-expanded",
      "true"
    );
  });

  if (this.internalTrigger) {
    this.internalTrigger.setAttribute(
      "aria-expanded",
      "true"
    );
  }


  /*
   * Lock page scroll
   */
  this.body.classList.add(
    this.activeOverlayBodyClass
  );


  /*
   * Focus search input
   */
  setTimeout(() => {

    if (
      this.input &&
      typeof this.input.focus === "function"
    ) {
      this.input.focus();
    }

  }, 50);
}

  /* ==============================================================
     INITIALIZE TRIGGERS
     ============================================================== */

  init() {
    /*
      --------------------------------------------------------------
      INTERNAL SEARCH TRIGGER
      --------------------------------------------------------------

      Example:

      <search-drawer>
        <div class="wt-header__search-trigger">
      */
    if (this.internalTrigger) {
      this.internalTrigger.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          this.toggleDrawerClasses();
        }
      );

      /*
        Keyboard accessibility for div role="button"
      */
      this.internalTrigger.addEventListener(
        "keydown",
        (event) => {
          if (
            event.key === "Enter" ||
            event.key === " "
          ) {
            event.preventDefault();
            this.toggleDrawerClasses();
          }
        }
      );
    }

    /*
      --------------------------------------------------------------
      EXTERNAL HEADER PANEL SEARCH BUTTON
      --------------------------------------------------------------

      Only bind buttons pointing to THIS drawer.
      */
    this.externalTriggers().forEach((trigger) => {
      /*
        Prevent duplicate binding.
      */
      if (
        trigger.dataset.searchDrawerBound === this.id
      ) {
        return;
      }

      trigger.dataset.searchDrawerBound = this.id;

      trigger.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          this.toggleDrawerClasses();
        }
      );
    });

    /* ============================================================
       CLEAR SEARCH BUTTON
       ============================================================ */

    if (this.clearButton && this.input) {
      this.clearButton.addEventListener(
        "click",
        (event) => {
          event.preventDefault();

          this.input.value = "";

          this.clearButton.style.display = "none";

          this.clearButton.setAttribute(
            "tabindex",
            "-1"
          );

          this.isVisibleClearButton = false;

          this.clearResults();

          this.input.focus();
        }
      );
    }

    /* ============================================================
       CLOSE BUTTON
       ============================================================ */

    if (this.closeButton) {
      this.closeButton.addEventListener(
        "click",
        (event) => {
          event.preventDefault();

          if (this.isOpen) {
            this.toggleDrawerClasses();
          }
        }
      );
    }

    /* ============================================================
       ESCAPE + TAB
       ============================================================ */

    this.addEventListener("keydown", (event) => {
      const isTabPressed =
        event.key === "Tab" ||
        event.keyCode === 9 ||
        event.code === "Tab";

      /*
        ESCAPE
      */
      if (
        event.key === "Escape" ||
        event.keyCode === 27 ||
        event.code === "Escape"
      ) {
        if (this.isOpen) {
          event.preventDefault();
          this.toggleDrawerClasses();
        }

        return;
      }

      /*
        TAB FOCUS TRAP
      */
      if (isTabPressed && this.isOpen) {
        const { first, last } =
          this.getFocusableElements();

        if (!first || !last) {
          return;
        }

        if (
          event.shiftKey &&
          document.activeElement === first
        ) {
          last.focus();
          event.preventDefault();

        } else if (
          !event.shiftKey &&
          document.activeElement === last
        ) {
          first.focus();
          event.preventDefault();
        }
      }
    });

    /* ============================================================
       INPUT CHANGE
       ============================================================ */

    if (this.input) {
      this.input.addEventListener(
        "input",
        () => {
          if (
            this.input.value.length > 0 &&
            !this.isVisibleClearButton
          ) {
            if (this.clearButton) {
              this.clearButton.style.display =
                "flex";

              this.clearButton.setAttribute(
                "tabindex",
                "0"
              );
            }

            this.isVisibleClearButton = true;

          } else if (
            this.input.value.length === 0 &&
            this.isVisibleClearButton
          ) {
            if (this.clearButton) {
              this.clearButton.style.display =
                "none";

              this.clearButton.setAttribute(
                "tabindex",
                "-1"
              );
            }

            this.isVisibleClearButton = false;
          }
        }
      );
    }
  }

  /* ==============================================================
     FORM / PREDICTIVE SEARCH
     ============================================================== */

  setupEventListeners() {
    const form = this.querySelector(
      "form.store-search-form"
    );

    if (form) {
      form.addEventListener(
        "submit",
        this.onFormSubmit
      );
    }

    if (this.input) {
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }

    /*
      Shopify Theme Editor
    */
    if (
      typeof Shopify !== "undefined" &&
      Shopify.designMode
    ) {
      document.addEventListener(
        "shopify:section:load",
        this.saveSuggestionMenuInDesignMode
      );
    }
  }

  /* ==============================================================
     SHOPIFY DESIGN MODE
     ============================================================== */

  saveSuggestionMenuInDesignMode() {
    if (
      this.body.classList.contains(
        this.activeOverlayBodyClass
      )
    ) {
      if (
        !this.classList.contains(
          this.classDrawerActive
        )
      ) {
        this.classList.add(
          this.classDrawerActive
        );
      }
    }
  }

  /* ==============================================================
     SEARCH QUERY
     ============================================================== */

  getQuery() {
    if (!this.input) {
      return "";
    }

    return this.input.value.trim();
  }

  /* ==============================================================
     SEARCH CHANGE
     ============================================================== */

  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length) {
      this.getSearchResults(searchTerm);

      this.removeAttribute("empty");
    } else {
      this.clearResults();
    }
  }

  /* ==============================================================
     FORM SUBMIT
     ============================================================== */

  onFormSubmit(event) {
    const query = this.getQuery();

    if (
      !query.length ||
      this.querySelector(
        '[aria-selected="true"] a'
      )
    ) {
      event.preventDefault();
    }
  }

  /* ==============================================================
     PREDICTIVE SEARCH
     ============================================================== */

  getSearchResults(searchTerm) {
    const queryKey = searchTerm
      .replace(/\s+/g, "-")
      .toLowerCase();

    this.setLiveRegionLoadingState();

    /*
      Cached result
    */
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(
        this.cachedResults[queryKey]
      );

      return;
    }

    const predictiveUrl =
      `${routes.predictive_search_url}` +
      `?q=${encodeURIComponent(searchTerm)}` +
      `&${encodeURIComponent("resources[type]")}=product,page,article,collection,query` +
      `&${encodeURIComponent("resources[limit_scope]")}=each` +
      `&${encodeURIComponent("resources[limit]")}=6` +
      `&section_id=predictive-search`;

    fetch(predictiveUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Predictive search failed: ${response.status}`
          );
        }

        return response.text();
      })
      .then((text) => {
        const documentResult =
          new DOMParser().parseFromString(
            text,
            "text/html"
          );

        const predictiveSection =
          documentResult.querySelector(
            "#shopify-section-predictive-search"
          );

        if (!predictiveSection) {
          throw new Error(
            "Predictive search markup not found."
          );
        }

        const resultsMarkup =
          predictiveSection.innerHTML;

        this.cachedResults[queryKey] =
          resultsMarkup;

        this.renderSearchResults(
          resultsMarkup
        );
      })
      .catch((error) => {
        console.error(
          "Search drawer error:",
          error
        );

        this.removeAttribute("loading");
      });
  }

  /* ==============================================================
     LIVE REGION
     ============================================================== */

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        ".predictive-search-status"
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        "data-loading-text"
      );

    if (this.statusElement) {
      this.setLiveRegionText(
        this.loadingText || ""
      );
    }

    this.setAttribute("loading", "true");
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) {
      return;
    }

    this.statusElement.setAttribute(
      "aria-hidden",
      "false"
    );

    this.statusElement.textContent =
      statusText || "";

    setTimeout(() => {
      if (this.statusElement) {
        this.statusElement.setAttribute(
          "aria-hidden",
          "true"
        );
      }
    }, 1000);
  }

  /* ==============================================================
     RENDER RESULTS
     ============================================================== */

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) {
      return;
    }

    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute("results", "true");

    this.setLiveRegionResults();
  }

  /* ==============================================================
     LIVE REGION RESULTS
     ============================================================== */

  setLiveRegionResults() {
    this.removeAttribute("loading");

    const countElement =
      this.querySelector(
        "[data-predictive-search-live-region-count-value]"
      );

    this.setLiveRegionText(
      countElement
        ? countElement.textContent
        : ""
    );
  }

  /* ==============================================================
     CLEAR RESULTS
     ============================================================== */

  clearResults() {
    if (this.input) {
      this.input.value = "";
    }

    this.removeAttribute("results");

    this.setAttribute("empty", "true");
  }
}


/* ================================================================
   DEFINE CUSTOM ELEMENT
   ================================================================ */

if (!customElements.get("search-drawer")) {
  customElements.define(
    "search-drawer",
    DrawerSearchSection
  );
}

// class DrawerSearchSection extends HTMLElement {
//   constructor() {
//     super();

//     this.isOpen = false;
//     this.drawerClass = "wt-drawer-search";
//     this.drawer = document.querySelector(`.${this.drawerClass}`);
//     this.classDrawerActive = `${this.drawerClass}--active`;
//     this.pageOverlayClass = "search-overlay";
//     this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
//     this.body = document.body;

//     this.triggerQuery = [
//       ".wt-header__search-trigger",
//       ".wt-header__search__close",
//     ].join(", ");
//     this.triggers = () => document.querySelectorAll(this.triggerQuery);

//     // search stuff
//     this.cachedResults = {};
//     this.input = this.querySelector('input[name="q"]');
//     this.clearButton = this.querySelector(".wt-header__search__clear-button");
//     this.closeButton = this.querySelector(".wt-header__search__close");
//     this.predictiveSearchResults = this.querySelector(
//       "[data-predictive-search]",
//     );
//     this.mainTrigger = this.querySelector(".wt-header__search-trigger");
//     this.emptyAnnouncement = this.querySelector(".search-empty");

//     this.isVisibleClearButton = false;

//     this.saveSuggestionMenuInDesignMode =
//       this.saveSuggestionMenuInDesignMode.bind(this);
//     this.setupEventListeners();

//     this.toggleTabindexElements = [this.input, this.closeButton];

//     this.init();
//   }

//   getFocusableElements() {
//     const focusableElementsSelector =
//       "button, [href], input:not([type='hidden']), select, [tabindex]";
//     const focusableElements = () =>
//       Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
//         (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
//       );

//     return {
//       focusableElements,
//       first: focusableElements()[0],
//       last: focusableElements()[focusableElements().length - 1],
//     };
//   }

//   // fixOverlayIssue() {
//   //     const body = this.body;
//   //
//   //     if(body.classList.contains(this.activeOverlayBodyClass)) {
//   //         const offsetTop = -parseInt(body.style.top);
//   //         body.style.position = 'initial';
//   //         body.style.top = 'initial';
//   //         body.style.left = 'initial';
//   //         window.scrollTo(0, offsetTop);
//   //
//   //     } else {
//   //         // body.style.top = -document.documentElement.scrollTop+'px';
//   //         // body.style.left = '0px';
//   //         // body.style.position = 'fixed';
//   //     }
//   // }

//   onToggle() {
//     if (this.hasAttribute("open")) {
//       this.removeAttribute("open");
//       setTabindex(this.toggleTabindexElements, "-1");
//       setTabindex([this.mainTrigger], "0");
//       this.isOpen = false;
//       setTimeout(() => {
//         this.mainTrigger.focus();
//       }, 0);
//     } else {
//       this.setAttribute("open", "");
//       setTabindex(this.toggleTabindexElements, "0");
//       setTabindex([this.mainTrigger], "-1");
//       this.input.focus();
//       this.isOpen = true;
//     }
//   }

//   toggleDrawerClasses() {
//     this.onToggle();
//     // this.fixOverlayIssue();
//     this.drawer.classList.toggle(this.classDrawerActive);
//     this.body.classList.toggle(this.activeOverlayBodyClass);
//   }

//   init() {
//     this.triggers().forEach((trigger) => {
//       trigger.addEventListener("click", (e) => {
//         e.preventDefault();
//         this.toggleDrawerClasses();
//       });
//     });

//     this.clearButton.addEventListener("click", () => {
//       this.input.value = "";
//       this.clearButton.style.display = "none";
//       this.isVisibleClearButton = false;
//       this.clearResults();
//     });

//     this.addEventListener("keydown", (e) => {
//       const isTabPressed =
//         e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
//       const { first, last } = this.getFocusableElements();

//       if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
//         if (this.isOpen) {
//           this.toggleDrawerClasses();
//         }
//       }

//       if (isTabPressed) {
//         if (this.isOpen) {
//           if (e.shiftKey && document.activeElement === first) {
//             last.focus();
//             e.preventDefault();
//           } else if (!e.shiftKey && document.activeElement === last) {
//             first.focus();
//             e.preventDefault();
//           }
//         }
//       }
//     });

//     this.input.addEventListener("input", () => {
//       if (this.input.value.length > 0 && !this.isVisibleClearButton) {
//         this.clearButton.style.display = "flex";
//         this.clearButton.setAttribute("tabindex", "0");
//         this.isVisibleClearButton = true;
//       } else if (this.input.value.length === 0 && this.isVisibleClearButton) {
//         this.clearButton.style.display = "none";
//         this.clearButton.setAttribute("tabindex", "-1");
//         this.isVisibleClearButton = false;
//       }
//     });
//   }

//   // search stuff
//   setupEventListeners() {
//     const form = this.querySelector("form.store-search-form");
//     form.addEventListener("submit", this.onFormSubmit.bind(this));
//     this.input.addEventListener(
//       "input",
//       debounce((event) => {
//         this.onChange(event);
//       }, 300).bind(this),
//     );

//     if (Shopify.designMode) {
//       document.addEventListener(
//         "shopify:section:load",
//         this.saveSuggestionMenuInDesignMode,
//       );
//     }
//   }

//   saveSuggestionMenuInDesignMode() {
//     if (this.body.classList.contains(this.activeOverlayBodyClass))
//       this.drawer.classList.toggle(this.classDrawerActive);
//   }

//   getQuery() {
//     return this.input.value.trim();
//   }

//   onChange() {
//     const searchTerm = this.getQuery();

//     if (searchTerm.length) {
//       this.getSearchResults(searchTerm);
//       this.removeAttribute("empty");
//     } else {
//       this.clearResults();
//     }
//   }

//   onFormSubmit(event) {
//     if (
//       !this.getQuery().length ||
//       this.querySelector('[aria-selected="true"] a')
//     )
//       event.preventDefault();
//   }

//   getSearchResults(searchTerm) {
//     const queryKey = searchTerm.replace(" ", "-").toLowerCase();
//     this.setLiveRegionLoadingState();

//     if (this.cachedResults[queryKey]) {
//       this.renderSearchResults(this.cachedResults[queryKey]);
//       return;
//     }

//     fetch(
//       `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent("resources[type]")}=product,page,article,collection,query&${encodeURIComponent("resources[limit_scope]")}=each&${encodeURIComponent("resources[limit]")}=6&section_id=predictive-search`,
//     )
//       .then((response) => {
//         if (!response.ok) {
//           let error = new Error(response.status);
//           throw error;
//         }
//         return response.text();
//       })
//       .then((text) => {
//         const resultsMarkup = new DOMParser()
//           .parseFromString(text, "text/html")
//           .querySelector("#shopify-section-predictive-search").innerHTML;
//         this.cachedResults[queryKey] = resultsMarkup;
//         this.renderSearchResults(resultsMarkup);
//       })
//       .catch((error) => {
//         throw error;
//       });
//   }

//   setLiveRegionLoadingState() {
//     this.statusElement =
//       this.statusElement || this.querySelector(".predictive-search-status");
//     this.loadingText =
//       this.loadingText || this.getAttribute("data-loading-text");

//     this.setLiveRegionText(this.loadingText);
//     this.setAttribute("loading", true);
//   }

//   setLiveRegionText(statusText) {
//     this.statusElement.setAttribute("aria-hidden", "false");
//     this.statusElement.textContent = statusText;

//     setTimeout(() => {
//       this.statusElement.setAttribute("aria-hidden", "true");
//     }, 1000);
//   }

//   renderSearchResults(resultsMarkup) {
//     this.predictiveSearchResults.innerHTML = resultsMarkup;

//     this.setAttribute("results", true);

//     this.setLiveRegionResults();
//   }

//   setLiveRegionResults() {
//     this.removeAttribute("loading");
//     this.setLiveRegionText(
//       this.querySelector("[data-predictive-search-live-region-count-value]")
//         ?.textContent,
//     );
//   }

//   clearResults() {
//     this.input.value = "";
//     this.removeAttribute("results");
//     this.setAttribute("empty", true);
//   }
// }

// customElements.define("search-drawer", DrawerSearchSection);
